import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// Initial State
const initialState = {
  inquiries: [],
  currentInquiry: null,
  currentPatient: null,
  isLoading: false,
  error: null,
  programs: [], // Added for programs list
};

/* ==============================
   ASYNC THUNKS
============================== */

// Fetch all inquiries (optionally by patient ID)
export const fetchInquiries = createAsyncThunk(
  'inquiries/fetchInquiries',
  async (patientId = null, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/inquiries/${patientId}`
        : '/inquiries';
      const response = await api.get(url);
      return response.data.data; // Adjusted to match controller response
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch inquiries'
      );
    }
  }
);

// Fetch list of programs
export const fetchPrograms = createAsyncThunk(
  "programs/fetchPrograms",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/programs');
      return response.data.data; // since backend returns { status, data }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch programs"
      );
    }
  }
);

// Fetch single inquiry by ID
export const fetchInquiry = createAsyncThunk(
  'inquiries/fetchInquiry',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/inquiries/show/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch inquiry'
      );
    }
  }
);

// Fetch inquiry by patient ID
export const fetchInquiryByPatient = createAsyncThunk(
  'inquiries/fetchInquiryByPatient',
  async (patientId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/patients/${patientId}/inquiry`);
      return response.data || null;
    } catch (error) {
      if (error.response?.status === 404) return null;
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch inquiry by patient'
      );
    }
  }
);

// Fetch patient data for inquiry
export const fetchPatientDataForInquiry = createAsyncThunk(
  "inquiries/fetchPatientDataForInquiry",
  async (patientId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/patients/${patientId}/inquiry-data`);
      return response.data.data; // backend returns { message, data }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch patient data for inquiry"
      );
    }
  }
);

// ✅ Enroll Inquiry as Patient (with auth token)
export const enrollInquiryAsPatient = createAsyncThunk(
  "inquiries/enrollInquiryAsPatient",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/inquiries/${id}/enroll`);
      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    }
  }
);

// Create new inquiry
export const createInquiry = createAsyncThunk(
  'inquiries/createInquiry',
  async ({ patientId, inquiryData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/inquiries/${patientId}`, inquiryData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create inquiry'
      );
    }
  }
);

// Create new client inquiry (no patient_id)
export const createClientInquiry = createAsyncThunk(
  'inquiries/createClientInquiry',
  async (inquiryData, { rejectWithValue }) => {
    try {
      const response = await api.post(`/client-inquiries`, inquiryData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create client inquiry'
      );
    }
  }
);

// Update inquiry
export const updateInquiry = createAsyncThunk(
  'inquiries/updateInquiry',
  async ({ id, inquiryData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/inquiries/${id}`, inquiryData);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || error.message || 'Failed to update inquiry'
      );
    }
  }
);

// Update existing client inquiry (based only on inquiry_id)
export const updateClientInquiry = createAsyncThunk(
  'inquiries/updateClientInquiry',
  async ({ id, inquiryData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/client-inquiries/${id}`, inquiryData);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update client inquiry'
      );
    }
  }
);

// Delete inquiry
export const deleteInquiry = createAsyncThunk(
  'inquiries/deleteInquiry',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/inquiries/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete inquiry'
      );
    }
  }
);

/* ==============================
   SLICE
============================== */

const inquirySlice = createSlice({
  name: 'inquiries',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentInquiry: (state, action) => {
      state.currentInquiry = action.payload;
    },
    clearCurrentInquiry: (state) => {
      state.currentInquiry = null;
    },
  },
  extraReducers: (builder) => {
    builder
      //FETCH ALL PROGRAMS
      .addCase(fetchPrograms.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPrograms.fulfilled, (state, action) => {
        state.isLoading = false;
        state.programs = action.payload; // ✅ renamed items → programs
      })
      .addCase(fetchPrograms.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // FETCH ALL
      .addCase(fetchInquiries.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchInquiries.fulfilled, (state, action) => {
        state.isLoading = false;
        state.inquiries = action.payload;
      })
      .addCase(fetchInquiries.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchInquiry.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchInquiry.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentInquiry = action.payload;
      })
      .addCase(fetchInquiry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchInquiryByPatient.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        console.log('fetchInquiryByPatient pending');
      })
      .addCase(fetchInquiryByPatient.fulfilled, (state, action) => {
        state.isLoading = false;
        const data = action.payload;

        console.log("fetchInquiryByPatient fulfilled, payload:", data);

        // ✅ Handle both nested and direct structures
        if (data && typeof data === "object") {
          state.currentInquiry = data.data || data;
        } else {
          state.currentInquiry = null;
        }

        state.error = null;
      })
      .addCase(fetchInquiryByPatient.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        console.log('fetchInquiryByPatient rejected, error:', action.payload);
      })
      // FETCH PATIENT DATA FOR INQUIRY
      .addCase(fetchPatientDataForInquiry.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPatientDataForInquiry.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentPatient = action.payload; // store fetched patient data
      })
      .addCase(fetchPatientDataForInquiry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // ENROLL Inquiry as Patient
      .addCase(enrollInquiryAsPatient.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(enrollInquiryAsPatient.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        // optional: update that inquiry's patient_id in list if you want
      })
      .addCase(enrollInquiryAsPatient.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      })

      // CREATE Patient Inquiry
      .addCase(createInquiry.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createInquiry.fulfilled, (state, action) => {
        state.isLoading = false;
        state.inquiries.unshift(action.payload);
      })
      .addCase(createInquiry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE CLIENT INQUIRY
      .addCase(createClientInquiry.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createClientInquiry.fulfilled, (state, action) => {
        state.isLoading = false;
        // Push new inquiry to the list
        state.inquiries.unshift(action.payload);
      })
      .addCase(createClientInquiry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE Patient Inquiry
      .addCase(updateInquiry.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateInquiry.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload;
        const updatedMasterId = updated?.master?.id ?? updated?.id;
        const index = state.inquiries.findIndex((i) => {
          const masterId = i?.master?.id ?? i?.id;
          return masterId === updatedMasterId;
        });
        if (index !== -1) {
          state.inquiries[index] = updated;
        }
        if (state.currentInquiry) {
          const currentMasterId = state.currentInquiry?.master?.id ?? state.currentInquiry?.id;
          if (currentMasterId === updatedMasterId) {
            state.currentInquiry = updated;
          }
        }
      })
      .addCase(updateInquiry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error?.message || 'Failed to update inquiry';
      })

      // UPDATE CLIENT INQUIRY
      .addCase(updateClientInquiry.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateClientInquiry.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload;
        const updatedId = updated?.master?.id ?? updated?.id;

        // Replace in the inquiries list
        const index = state.inquiries.findIndex((i) => {
          const inquiryId = i?.master?.id ?? i?.id;
          return inquiryId === updatedId;
        });
        if (index !== -1) {
          state.inquiries[index] = updated;
        }

        // If this inquiry is the one currently open, update it too
        if (state.currentInquiry) {
          const currentId = state.currentInquiry?.master?.id ?? state.currentInquiry?.id;
          if (currentId === updatedId) {
            state.currentInquiry = updated;
          }
        }
      })
      .addCase(updateClientInquiry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error?.message || 'Failed to update client inquiry';
      })

      // DELETE
      .addCase(deleteInquiry.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteInquiry.fulfilled, (state, action) => {
        state.isLoading = false;
        state.inquiries = state.inquiries.filter(
          (i) => i.master.id !== action.payload
        );
        if (state.currentInquiry?.master.id === action.payload) {
          state.currentInquiry = null;
        }
      })
      .addCase(deleteInquiry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setCurrentInquiry, clearCurrentInquiry } =
  inquirySlice.actions;

export default inquirySlice.reducer;
